import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";

export default function Login() {
    const { t } = useTranslation();
    const { user, login } = useApp();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/", { replace: true });
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-white">
            {/* ───────────────── Hero / brand panel ───────────────── */}
            <div className="relative hidden lg:block overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/5779775/pexels-photo-5779775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600"
                    alt={t("login.heroAlt", "Two chefs working in a commercial kitchen")}
                    className="absolute inset-0 h-full w-full object-cover scale-[1.02]"
                />
                {/*
                  Premium two-stop gradient: deep at the bottom where the
                  slogan sits, gentler at the top where the logo lives. Keeps
                  the kitchen image clearly visible while guaranteeing AA
                  contrast for the headline.
                */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-b from-neutral-950/45 via-neutral-950/70 to-neutral-950/90"
                />
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(120%_70%_at_50%_20%,transparent_0%,rgba(10,10,10,0.4)_100%)]"
                />

                <div className="relative h-full flex flex-col justify-between p-10 xl:p-16 2xl:p-20 text-white">
                    <div className="leading-tight">
                        <div className="font-brand text-3xl xl:text-4xl 2xl:text-5xl font-semibold tracking-tight">
                            MiseOne
                        </div>
                        <div className="mt-2 text-[10px] xl:text-[11px] uppercase tracking-[0.32em] text-white/70">
                            {t("login.digitalMiseEnPlace", "The Digital Mise en Place")}
                        </div>
                    </div>

                    <div className="max-w-2xl">
                        <div className="h-px w-16 bg-white/40 mb-8" aria-hidden="true" />
                        <h1 className="font-display font-extrabold leading-[0.98] tracking-tight
                                         text-[clamp(3rem,5.5vw,5.5rem)] !text-white
                                         drop-shadow-[0_2px_18px_rgba(0,0,0,0.65)]">
                            {t("login.heroLess", "Less\u00a0work.")}<br />
                            {t("login.heroMore", "More\u00a0cooking.")}
                        </h1>
                        <p className="mt-7 text-base xl:text-lg text-white/90 max-w-md leading-relaxed
                                       drop-shadow-[0_1px_8px_rgba(0,0,0,0.55)]">
                            {t("login.heroLine1", "The kitchen operations platform designed by chefs, for chefs.")}
                            {" "}
                            {t("login.heroLine2", "Plan menus, track deliveries, run daily checks — without the paperwork.")}
                        </p>
                    </div>

                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/55">
                        {t("login.builtFor", "Built for professional kitchens · Helsinki")}
                    </div>
                </div>
            </div>

            {/* ───────────────── Form panel ───────────────── */}
            <div className="flex flex-col items-center justify-center px-4 sm:px-6 py-6 bg-white overflow-hidden">
                {/*
                  Logo container: spans the panel width so the artwork
                  can grow much larger than the form below. Transparent,
                  no box, no border — just the logo on white. Negative
                  margin pulls "Welcome back, chef." up tight.
                */}
                <div className="w-full flex justify-center">
                    <img
                        src="/mise-one-logo.png"
                        alt="MiseOne — The Digital Mise en Place"
                        data-testid="login-form-logo"
                        className="w-56 sm:w-64 md:w-72 h-auto object-contain select-none"
                        draggable="false"
                    />
                </div>

                <div className="w-full max-w-md mt-2">

                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.01em] text-center text-neutral-900">
                        {t("login.welcome", "Welcome back, chef.")}
                    </h2>
                    <p className="mt-3 text-sm text-neutral-500 text-center">
                        {t("login.subtitle", "Sign in to your kitchen workstation.")}
                    </p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700 mb-2">
                                {t("login.email", "Email")}
                            </label>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full h-12 px-3.5 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/5 transition"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700 mb-2">
                                {t("login.password", "Password")}
                            </label>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full h-12 px-3.5 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/5 transition"
                            />
                        </div>
                        {error && (
                            <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                {error}
                            </div>
                        )}
                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-md bg-neutral-900 text-white font-semibold tracking-tight hover:bg-neutral-800 disabled:opacity-60 transition shadow-sm"
                        >
                            {loading ? t("login.signingIn", "Signing in…") : t("login.signIn", "Sign in")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
